import * as React from 'react'
import { Slot } from 'primitives/slot'
import { useActivateTrialRootContext } from './activate-trial-root'
import { AnimatedSpinnerIcon } from 'primitives/icons/animated-spinner-icon'
import { tw } from 'utils/classnames'

interface ActivateTrialTriggerProps {
  asChild?: boolean
  children?: React.ReactNode
  className?: string
  style?: React.CSSProperties
  testId?: string
}

export function ActivateTrialTrigger({
  asChild = false,
  children,
  className,
  style,
  testId,
}: ActivateTrialTriggerProps) {
  const { getLoggedOutHref, handleLoggedInTrigger, isPending, isUserLoggedIn } =
    useActivateTrialRootContext()

  if (isUserLoggedIn) {
    if (asChild) {
      return (
        <Slot
          className={tw('relative', className)}
          style={style}
          data-testid={testId}
          aria-disabled={isPending || undefined}
          onClick={(event: React.MouseEvent<HTMLElement>) => {
            if (isPending) {
              event.preventDefault()
              return
            }

            handleLoggedInTrigger()
          }}
        >
          {isPending ? (
            <span className="absolute inset-0 flex items-center justify-center">
              <AnimatedSpinnerIcon />
            </span>
          ) : null}
          <span className={isPending ? 'invisible' : 'visible'}>{children}</span>
        </Slot>
      )
    }

    return (
      <button
        type="button"
        disabled={isPending}
        onClick={() => {
          void handleLoggedInTrigger()
        }}
        data-testid={testId}
        style={style}
        className={tw('relative', className)}
      >
        {isPending ? (
          <span className="absolute inset-0 flex items-center justify-center">
            <AnimatedSpinnerIcon />
          </span>
        ) : null}
        <span className={isPending ? 'invisible' : 'visible'}>{children}</span>
      </button>
    )
  }

  if (asChild) {
    const loggedOutSlotProps = {
      className,
      style,
      'data-testid': testId,
      href: getLoggedOutHref(),
    } as React.ComponentPropsWithoutRef<typeof Slot>

    return <Slot {...loggedOutSlotProps}>{children}</Slot>
  }

  return (
    <a data-testid={testId} href={getLoggedOutHref()} style={style} className={className}>
      {children}
    </a>
  )
}
